/* Yatskia: standard product forms redirect to the cart after a confirmed add.
 * Replace assets/product-form.js in full. Do not load a second copy.
 * The separate product personalization handler keeps control of its own forms.
 */
(() => {
  'use strict';

  if (customElements.get('product-form')) return;

  const state = {
    owner: null,
    reviewRequired: false,
    navigating: false,
    reviewVisit: false
  };

  const arrivalKey = 'yatskia:cart-arrival:v1';

  function route(name) {
    const root = window.Shopify?.routes?.root || '/';

    const fallback =
      root + (name === 'cart_url' ? 'cart' : 'cart/add.js');

    const url = new URL(
      window.routes?.[name] || fallback,
      window.location.origin
    );

    if (url.origin !== window.location.origin) {
      throw new Error('Invalid cart route.');
    }

    return url.pathname + url.search;
  }

  function notify(name, detail) {
    try {
      if (
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined'
      ) {
        const topic = PUB_SUB_EVENTS[name];

        if (topic) {
          Promise.resolve(publish(topic, detail)).catch(() => {});
        }
      }
    } catch {
      // An optional subscriber must not change the cart request's result.
    }
  }

  class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.onSubmitHandler = this.onSubmitHandler.bind(this);
    }

    connectedCallback() {
      // Allow children to finish parsing after dynamic insertion.
      queueMicrotask(() => {
        if (!this.isConnected || this.bound) return;

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');

        if (!this.form || !this.submitButton || !this.variantIdInput) {
          return;
        }

        this.bound = true;

        this.submitButtonText =
          this.submitButton.querySelector('span');

        this.spinner =
          this.querySelector('.loading__spinner');

        this.variantIdInput.disabled = false;

        this.submitButton.removeAttribute('aria-haspopup');

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler
        );
      });
    }

    disconnectedCallback() {
      this.form?.removeEventListener(
        'submit',
        this.onSubmitHandler
      );

      this.bound = false;

      // Do not retry or cancel an add merely because a quick-view modal closes.
    }

    get variantIdInput() {
      return this.form?.querySelector('[name="id"]');
    }

    setBusy(value) {
      this.busy = value;

      this.form?.setAttribute(
        'aria-busy',
        String(value)
      );

      this.submitButton?.classList.toggle(
        'loading',
        value
      );

      this.spinner?.classList.toggle(
        'hidden',
        !value
      );

      if (value) {
        this.submitButton?.setAttribute(
          'aria-disabled',
          'true'
        );
      } else {
        this.submitButton?.removeAttribute(
          'aria-disabled'
        );
      }
    }

    handleErrorMessage(message = false, review = false) {
      this.errorMessageWrapper ||=
        this.querySelector(
          '.product-form__error-message-wrapper'
        );

      this.errorMessage ||=
        this.errorMessageWrapper?.querySelector(
          '.product-form__error-message'
        );

      if (!this.errorMessageWrapper || !this.errorMessage) {
        if (!message) return;

        this.errorMessageWrapper =
          document.createElement('div');

        this.errorMessageWrapper.className =
          'product-form__error-message-wrapper';

        this.errorMessage =
          document.createElement('span');

        this.errorMessage.className =
          'product-form__error-message';

        this.errorMessageWrapper.append(
          this.errorMessage
        );

        this.form.append(
          this.errorMessageWrapper
        );
      }

      this.errorMessageWrapper.setAttribute(
        'role',
        'alert'
      );

      this.errorMessageWrapper.hidden = !message;

      this.errorMessage.textContent =
        message || '';

      this.reviewLink?.remove();

      if (!message || !review) return;

      this.reviewLink =
        document.createElement('a');

      this.reviewLink.href =
        route('cart_url');

      this.reviewLink.className =
        'link underlined-link';

      this.reviewLink.textContent =
        'View cart';

      this.reviewLink.addEventListener(
        'click',
        (event) => {
          if (
            event.button === 0 &&
            !event.ctrlKey &&
            !event.metaKey &&
            !event.shiftKey &&
            !event.altKey
          ) {
            state.reviewVisit = true;
          }
        }
      );

      this.errorMessage.append(
        document.createTextNode(' '),
        this.reviewLink
      );
    }

    toggleSubmitButton(disable = true, text) {
      if (!this.submitButton) return;

      this.submitButton.disabled = disable;

      if (this.submitButtonText) {
        const label =
          text ||
          (!disable && window.variantStrings?.addToCart);

        if (label) {
          this.submitButtonText.textContent = label;
        }
      }
    }

    async onSubmitHandler(event) {
      // Respect capture-phase personalization validation
      // and accelerated checkout.
      if (
        event.defaultPrevented ||
        event.submitter?.name === 'checkout'
      ) {
        return;
      }

      event.preventDefault();

      if (
        state.reviewRequired ||
        state.navigating
      ) {
        this.handleErrorMessage(
          'Please review your cart before adding again.',
          true
        );

        return;
      }

      if (state.owner || this.busy) {
        if (state.owner !== this) {
          this.handleErrorMessage(
            'Please wait while your item is added.'
          );
        }

        return;
      }

      if (
        this.submitButton.disabled ||
        this.submitButton.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }

      if (!this.form.reportValidity()) return;

      const guard = window.YatskiaCartGuard;

      if (guard?.blocked()) {
        guard.explain();
        return;
      }

      let body;
      let addURL;
      let cartURL;
      let variant;
      let quantity;

      try {
        body = new FormData(this.form);

        variant =
          String(body.get('id') || '').trim();

        quantity =
          Number(body.get('quantity') || 1);

        if (!/^\d+$/.test(variant)) {
          throw new Error(
            'Please select an available option.'
          );
        }

        if (
          !Number.isSafeInteger(quantity) ||
          quantity < 1
        ) {
          throw new Error(
            'Please enter a valid quantity.'
          );
        }

        addURL = route('cart_add_url');
        cartURL = route('cart_url');

        // Preserve variant, quantity, selling plan,
        // uploads and all properties.
        // The destination page renders the cart,
        // so no popup sections are needed.
        body.delete('sections');
        body.delete('sections_url');
        body.delete('return_to');
      } catch (error) {
        this.handleErrorMessage(error.message);
        return;
      }

      state.owner = this;
      this.error = false;

      this.handleErrorMessage();
      this.setBusy(true);

      const controller =
        new AbortController();

      const timeout = setTimeout(
        () => controller.abort(),
        30000
      );

      let confirmed = false;

      try {
        const response = await fetch(addURL, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body,
          signal: controller.signal
        });

        const data = await response
          .json()
          .catch(() => null);

        if (
          !response.ok ||
          Number(data?.status) >= 400
        ) {
          throw new Error(
            typeof data?.description === 'string'
              ? data.description
              : 'Your item could not be added as requested.'
          );
        }

        const lines = Array.isArray(data?.items)
          ? data.items
          : [data];

        const added = lines.find(
          (line) =>
            line &&
            String(line.variant_id ?? line.id) === variant
        );

        if (
          !added ||
          !Number.isSafeInteger(added.quantity) ||
          added.quantity < quantity
        ) {
          throw new Error(
            'We could not confirm the requested item and quantity.'
          );
        }

        confirmed = true;
        state.navigating = true;

        notify('cartUpdate', {
          source: 'product-form',
          productVariantId: variant,
          cartData: data
        });

        // A later animation asset can consume this
        // one-time, non-personal marker.
        // Storage failure must never prevent navigation.
        try {
          sessionStorage.setItem(
            arrivalKey,
            JSON.stringify({
              at: Date.now(),
              path: cartURL
            })
          );
        } catch {}

        window.location.assign(cartURL);
      } catch (error) {
        this.error = true;

        state.navigating = false;
        state.reviewRequired = true;

        // Even an inventory error may partially add stock.
        // Never auto-resubmit.
        const message = confirmed
          ? 'Your item was added. Open your cart to continue.'
          : error.name === 'AbortError' ||
              error instanceof TypeError
            ? 'We could not confirm the result. Check your cart before adding again.'
            : `${error.message} Check your cart before adding again.`;

        this.handleErrorMessage(
          message,
          true
        );

        notify('cartError', {
          source: 'product-form',
          productVariantId: variant,
          message,
          errors: message
        });
      } finally {
        clearTimeout(timeout);

        state.owner = null;

        // Keep repeat additions blocked while
        // the confirmed navigation starts.
        if (!state.navigating) {
          this.setBusy(false);
        }
      }
    }
  }

  customElements.define(
    'product-form',
    ProductForm
  );

  window.addEventListener(
    'pageshow',
    (event) => {
      if (
        !event.persisted ||
        (!state.navigating && !state.reviewVisit)
      ) {
        return;
      }

      state.navigating = false;
      state.reviewRequired = false;
      state.reviewVisit = false;
      state.owner = null;

      document
        .querySelectorAll('product-form')
        .forEach((form) => {
          form.setBusy?.(false);
          form.handleErrorMessage?.();
        });
    }
  );
})();